import { useSharedAPI } from "@certn/api";
import { Key, useMemo, FocusEvent } from "react";
import { useTranslation } from "react-i18next";

import { SelectItem } from "../../components/inputs/select/SelectItem";
import { SearchableSelect } from "../../components/inputs/select/SearchableSelect";

import { FieldErrorsProps } from "../../components/inputs/_assets/input-wrapper/FieldErrors";

type AdministrativeDivision2USSelectProps = {
  label: string;
  country?: string;
  administrativeDivision1?: string;
  administrativeDivision2?: string;
  testID: string;
  onChange?: (name: string) => void;
  errors?: FieldErrorsProps["children"];
  isInvalid?: boolean;
  onBlur?: (e: FocusEvent<Element>) => void;
  isDisabled?: boolean;
  placeholder?: string;
  description?: string;
  isRequired?: boolean;
};

export function AdministrativeDivision2USSelect(
  props: AdministrativeDivision2USSelectProps
) {
  const { t } = useTranslation();
  const {
    testID = "administrative-division-2-select",
    placeholder = `${t("Select")} ${props.label.split(" ")[0]?.toLowerCase()}`,
    isRequired = true,
  } = props;

  // Only fetch counties for US and when a state is selected
  const enabled = props.country === "US" && props.administrativeDivision1;
  const emptyMessage = t("Please select");

  const { data: counties, isLoading } = useSharedAPI(
    enabled ? "/api/internal/shared/country-administrative-division-2/" : null,
    {
      country: props.country,
      administrative_division_1: props.administrativeDivision1,
    }
  );

  const sortedCounties = useMemo(() => {
    if (!(enabled && counties)) return [];

    // Exclude the "Other" option for US counties. All US jurisdictions have a
    // real county, and selecting "Other" causes downstream US County Criminal
    // Court Record search fulfillment to fail.
    return counties
      .filter((county) => county.code !== "OTHER")
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [counties, enabled]);

  return (
    <SearchableSelect
      {...props}
      testID={testID}
      isRequired={isRequired}
      isDisabled={
        props.isDisabled || isLoading || !enabled || !sortedCounties?.length
      }
      placeholder={placeholder}
      onSelectionChange={(selectedKey: Key | null) => {
        // find the select item by key and pass the human readable version of the county to the calling component
        // Example LOS_ANGELES --> Los Angeles
        const selected = sortedCounties.find(
          (item) => item.code === selectedKey
        );
        props.onChange?.(selected?.name ?? "");
      }}
      selectedKey={
        sortedCounties.find(
          (item) => item.name === props.administrativeDivision2
        )?.code ?? ""
      }
      items={sortedCounties}
      validationBehavior="aria"
      emptyMessage={emptyMessage}
    >
      {(item) => (
        <SelectItem key={item.code} id={item.code} textValue={item.name}>
          {item.name}
        </SelectItem>
      )}
    </SearchableSelect>
  );
}
